import { Link, useLocation } from '@tanstack/react-router';
import { ChevronLeft, HelpCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/cn';

export interface NavItem { to: string; label: string; icon: React.ReactNode; badge?: number; }
/** `label` is retained for call-site grouping, but the rail renders one flat list (per design). */
export interface NavGroup { label: string; items: NavItem[]; }

interface AppSidebarProps {
  navGroups: NavGroup[];
  storageKey: string;
  settingsPath?: string;
  autoCollapsePattern?: RegExp;
  isActive?: (to: string, pathname: string) => boolean;
}

const defaultIsActive = (to: string, pathname: string) =>
  pathname === to || pathname.startsWith(to + '/');

export function AppSidebar({
  navGroups,
  storageKey,
  autoCollapsePattern,
  isActive = defaultIsActive,
}: AppSidebarProps) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(storageKey) === '1'
  );
  const autoCollapsedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(storageKey, collapsed ? '1' : '0');
  }, [collapsed, storageKey]);

  useEffect(() => {
    if (autoCollapsePattern?.test(location.pathname)) {
      autoCollapsedRef.current = true;
      setCollapsed(true);
    } else if (autoCollapsedRef.current) {
      autoCollapsedRef.current = false;
      setCollapsed(false);
    }
  }, [location.pathname, autoCollapsePattern]);

  const items = navGroups.flatMap((group) => group.items);

  return (
    <aside
      className={cn(
        'relative flex flex-shrink-0 flex-col border-r border-[#f1f1f1] bg-white transition-[width] duration-200',
        collapsed ? 'w-[72px]' : 'w-[240px]'
      )}
    >
      {/* Logo header — 90px, matching the design's header band */}
      <div
        className={cn(
          'flex h-[90px] shrink-0 items-center border-b border-[#f1f1f1]',
          collapsed ? 'justify-center px-3' : 'px-6'
        )}
      >
        {collapsed ? (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            title="Expand sidebar"
            className="rounded-lg focus:outline-none"
          >
            <img src="/favicon.svg" alt="KodPrep" className="h-9 w-9 rounded-lg" />
          </button>
        ) : (
          <>
            <img
              src="/images/logo.svg"
              alt="KodPrep — Code. Learn. Excel."
              width={126}
              height={54}
              className="h-[54px] w-[126px] shrink-0"
            />
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              title="Collapse sidebar"
              className="ml-auto flex items-center justify-center rounded-md p-1 text-[#9ca3af] transition-colors hover:bg-[#f3f4f6] hover:text-[#6b7280]"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {/* Nav — one flat list of pills */}
      <nav
        className={cn(
          'flex flex-1 flex-col gap-1 overflow-y-auto py-5',
          collapsed ? 'px-3' : 'px-4'
        )}
      >
        {items.map((item) => {
          const active = isActive(item.to, location.pathname);
          return (
            <Link
              key={item.to}
              to={item.to as any}
              title={collapsed ? item.label : undefined}
              className={cn(
                'flex items-center rounded-[14px] transition-colors',
                collapsed
                  ? 'mx-auto h-11 w-11 justify-center'
                  : 'gap-3 px-[14px] py-[11px] text-[14px] leading-[21px]',
                active
                  ? 'bg-[#fff4f0] font-semibold text-[#111827]'
                  : 'font-normal text-[#6b7280] hover:bg-[#f9fafb] hover:text-[#111827]'
              )}
            >
              <span
                className={cn(
                  'relative flex shrink-0 items-center justify-center',
                  active ? 'text-[#FF5A1F]' : 'text-[#9CA3AF]'
                )}
              >
                {item.icon}
                {collapsed && !!item.badge && (
                  <span className="absolute -right-1.5 -top-1.5 h-2 w-2 rounded-full bg-[#FF5A1F]" />
                )}
              </span>
              {!collapsed && (
                <>
                  <span className="flex-1 truncate">{item.label}</span>
                  {!!item.badge && (
                    <span className="ml-auto flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#FF5A1F] px-1 text-[10px] font-semibold leading-none text-white">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Need Help card */}
      <div className={cn('pb-6', collapsed ? 'px-3' : 'px-4')}>
        {collapsed ? (
          <Link
            to="/help"
            title="Need Help? — Support center"
            className="mx-auto flex h-11 w-11 items-center justify-center rounded-[14px] border border-[#ececec] bg-white transition-colors hover:bg-[#f9fafb]"
          >
            <HelpCircle className="h-4 w-4 text-[#9ca3af]" />
          </Link>
        ) : (
          <Link
            to="/help"
            className="flex w-full items-center gap-[10px] rounded-[14px] border border-[#ececec] bg-white px-[17px] py-[15px] text-left transition-colors hover:bg-[#f9fafb]"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[#f3f4f6]">
              <HelpCircle className="h-4 w-4 text-[#9ca3af]" />
            </span>
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold leading-[19.5px] text-[#111827]">Need Help?</span>
              <span className="block text-[11px] leading-[16.5px] text-[#9ca3af]">Support center</span>
            </span>
          </Link>
        )}
      </div>
    </aside>
  );
}
